import { createBrowserHistory, History } from 'history';
import { configureStore } from '@reduxjs/toolkit';
import { Hub, Amplify } from '@aws-amplify/core';
import { CognitoConfiguration, getConfiguration } from './GlobalFunctions';
import user from './UserManagement';
import classrooms from './Classrooms';
import messages from './Messages';
import lessons from './Lessons';
import { session } from './Session';
import events, { push, AuthEvent } from './AuthenticationEvents';
import audioPlayer from './components/AudioPlayer/AudioPlayer';

const store = configureStore({
  reducer: {
    audioPlayer,
    user,
    classrooms,
    messages,
    lessons,
    session,
    events,
  },
  // CAUTION: we are explicitly turning off the warnings about putting in
  // non-serizliable objects into the store. This is because we are using
  // the CognitoUserProfileType which is an object with functions that we want
  // access to such as getSignInUserSession.
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }),
});

export const myBrowserHistory: History = createBrowserHistory();
export const cognitoConfiguration = getConfiguration('cognito') as CognitoConfiguration;

// Import data residency aware initialization
import { initializeDataResidencyAwareAuth } from './DataResidencyAwareAuthHelpers';

// Make this async to support data residency initialization
export async function initializeApplicationState() {
  console.debug(`Initializing application state with data residency awareness`);

  try {
    // Initialize with data residency awareness
    // This will detect the stored region and configure Amplify appropriately
    await initializeDataResidencyAwareAuth();
    console.log('✅ Data residency aware auth initialized');

    // Note: Hub listener is now set up in DataResidencyAwareAuthHelpers to prevent duplicates
  } catch (error) {
    console.error('❌ Failed to initialize data residency aware auth, falling back to default:', error);
    // If data residency initialization fails, fall back to original behavior
    configureAmplify();
  }
}

function configureAmplify(newSignInCallbackPath?: string) {
  const callbackUrlRoot = `${document.location.protocol}//${document.location.host}`;
  const signInCallback = `${callbackUrlRoot}${
    newSignInCallbackPath || cognitoConfiguration.hostedUISignInCallbackPath
  }`;
  const signOutCallback = `${callbackUrlRoot}${cognitoConfiguration.hostedUISignOutCallbackPath}`;
  console.info(`%c Configuring amplify`, 'background: #222; color: #bada55');

  const amplifyConfig: any = {
    region: cognitoConfiguration.region,
    authenticationFlowType: 'USER_PASSWORD_AUTH',
    Auth: {
      userPoolId: cognitoConfiguration.userPoolId,
      identityPoolId: cognitoConfiguration.identityPool,
      userPoolWebClientId: cognitoConfiguration.clientId,
    },
  };

  // Only add OAuth config if the domain is defined (US region has it, UAE doesn't)
  if (cognitoConfiguration.hostedUICognitoDomain) {
    amplifyConfig.oauth = {
      domain: cognitoConfiguration.hostedUICognitoDomain,
      scope: ['phone', 'email', 'profile', 'openid', 'aws.cognito.signin.user.admin'],
      redirectSignIn: signInCallback,
      redirectSignOut: signOutCallback,
      responseType: 'code', // or 'token', note that REFRESH token will only be generated when the responseType is code
    };
    console.log('✅ OAuth configured for this region');
  } else {
    console.warn('⚠️ OAuth not available for this region (likely UAE)');
  }

  Amplify.configure(amplifyConfig);

  Hub.listen('auth', pushEventIntoQueue);
}

function pushEventIntoQueue({ payload }: { payload: AuthEvent }) {
  console.info(`%c OauthLogin pushing event `, 'background: #222; color: #bada55', payload);

  getStore().dispatch(push(payload));
}

export function getStore() {
  return store;
}

// Export push for use in DataResidencyAwareAuthHelpers
export { push } from './AuthenticationEvents';

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;
